/** Ordering (Demo Mode only) - brings Order Channels, Standing Orders/Auto Shipments and
 * Promotions together as a single Procurement "Ordering" destination per the Stage 5 spec,
 * rather than three separate top-level nav entries. Each card launches into its existing real
 * screen instead of duplicating that functionality here.
 * lazy: launch-tile hub is a simplification - upgrade path is to embed each area inline (tabs)
 * once Ordering needs cross-area filtering/reporting. */

import { Box, Button, Grid, SvgIcon, Typography } from '@mui/material';
import { StyleOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CoinsHand, Truck02 } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';

const OrderingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('procurementOrdering');

  const TILES = [
    {
      key: 'orderChannels',
      label: t('ordering.tileOrderChannels', 'Order Channels'),
      description: t('ordering.tileOrderChannelsDescription', 'Define how each site orders from a supplier - integrated ordering, portal, EDI, email, manual upload or phone.'),
      href: '/procurement/order-channels',
      icon: <StyleOutlined />,
      accentColor: '#1976d2',
    },
    {
      key: 'standingOrders',
      label: t('ordering.tileStandingOrders', 'Standing Orders / Auto Shipments'),
      description: t('ordering.tileStandingOrdersDescription', 'Repeat supply patterns for predictable stock without manual ordering.'),
      href: '/manage-autoshipments/templates',
      icon: <SvgIcon><Truck02 /></SvgIcon>,
      accentColor: '#2e7d32',
    },
    {
      key: 'promotions',
      label: t('ordering.tilePromotions', 'Promotions'),
      description: t('ordering.tilePromotionsDescription', 'Promotional products, effective dates, price impact and supplier funding.'),
      href: '/promotions',
      icon: <SvgIcon><CoinsHand /></SvgIcon>,
      accentColor: '#ed6c02',
    },
  ];

  return (
    <PageArea>
      <PageTitle subTitle={t('ordering.pageSubtitle', 'Order channels, standing orders/auto shipments and promotions in one place')}>
        {t('ordering.pageTitle', 'Ordering')}
      </PageTitle>

      <Grid container
        spacing={2}
        sx={{ mt: 2 }}>
        {TILES.map((tile) => (
          <Grid item
            key={tile.key}
            xs={12}
            md={4}>
            <Section style={{ p: 4, height: '100%' }}>
              <Box display="flex"
                flexDirection="column"
                height="100%"
                justifyContent="space-between">
                <Box>
                  <Box sx={{
                    backgroundColor: `${tile.accentColor}20`,
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tile.accentColor,
                    mb: 2,
                  }}>
                    <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                  </Box>
                  <Typography variant="textMD"
                    sx={{ fontWeight: 600, mb: 1 }}>{tile.label}</Typography>
                  <Typography variant="textSM"
                    color="text.secondary">{tile.description}</Typography>
                </Box>
                <Button
                  variant="secondary"
                  size="lg"
                  sx={{ mt: 3, alignSelf: 'flex-start' }}
                  onClick={() => navigate(tile.href)}
                >
                  {t('ordering.actionOpen', 'Open')}
                </Button>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>
    </PageArea>
  );
};

export default OrderingPage;
